import { useCallback, useEffect, useState } from "react";
import { useGameStore } from "../store/useGameStore";
import { useRouletteStore } from "../store/useRouletteStore";
import { RouletteBoard } from "../components/RouletteBoard";

const SKIP_ANIMATION_KEY = "mk_roulette_skip_animation";

export function RouletteScreen() {
  const { setScreen, refreshBalance } = useGameStore();
  const { status, loading, spinning, error, lastResult, refresh, spin, clearError, clearResult } =
    useRouletteStore();

  const [skipAnimation, setSkipAnimation] = useState(
    () => localStorage.getItem(SKIP_ANIMATION_KEY) === "1",
  );
  const [pendingWinSlot, setPendingWinSlot] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleSkipChange = (checked: boolean) => {
    setSkipAnimation(checked);
    localStorage.setItem(SKIP_ANIMATION_KEY, checked ? "1" : "0");
  };

  const handleAnimationComplete = useCallback(
    (_slot: number) => {
      setShowResult(true);
      void refreshBalance();
    },
    [refreshBalance],
  );

  const handleSpin = async (): Promise<number> => {
    setShowResult(false);
    setPendingWinSlot(null);
    clearResult();
    const result = await spin();
    setPendingWinSlot(result.winning_slot_index);
    return result.winning_slot_index;
  };

  if (loading && !status) {
    return (
      <div className="p-4 text-center text-gray-400">
        Loading Fortune Wheel…
      </div>
    );
  }

  if (!status?.active) {
    return (
      <div className="p-4 space-y-4">
        <Header onBack={() => setScreen("main")} />
        <p className="text-gray-400 text-sm text-center">Fortune Wheel is not active right now.</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-8">
      <Header onBack={() => setScreen("main")} />

      <div className="text-center">
        <h1 className="text-xl font-bold font-game text-gold-400">Fortune Wheel</h1>
        <p className="text-gray-400 text-xs mt-1">Spin for a chance to win prizes!</p>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-500/40 bg-rose-950/40 px-3 py-2 text-sm text-rose-300">
          {error}
          <button type="button" onClick={clearError} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      <RouletteBoard
        segments={status.segments}
        spinCost={status.spin_cost}
        costAvailable={status.cost_available}
        spinning={spinning || pendingWinSlot !== null}
        skipAnimation={skipAnimation}
        pendingWinSlot={pendingWinSlot}
        onSpin={handleSpin}
        onAnimationComplete={(slot) => {
          setPendingWinSlot(null);
          handleAnimationComplete(slot);
        }}
      />

      <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer justify-center">
        <input
          type="checkbox"
          checked={skipAnimation}
          onChange={(e) => handleSkipChange(e.target.checked)}
          className="rounded border-kingdom-mid"
        />
        Пропустить анимацию
      </label>

      {showResult && lastResult && (
        <div className="rounded-xl border border-gold-500/50 bg-kingdom-purple p-4 text-center space-y-2">
          <div className="text-3xl">🎉</div>
          <p className="font-bold text-gold-400">{lastResult.message}</p>
          <p className="text-gray-400 text-xs">{lastResult.reward.label}</p>
        </div>
      )}
    </div>
  );
}

function Header({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onBack}
        className="text-gray-400 hover:text-white border border-kingdom-mid px-3 py-1.5 rounded-lg text-sm"
      >
        ← Back
      </button>
    </div>
  );
}
